import { useEffect, useMemo, useState } from "react";

import { api } from "../api/client";
import { useApiList, useMutation } from "../api/hooks";
import type { LocaleSummary, TranslationEntry } from "../api/types";
import { Badge, Page, Toolbar, useToast } from "../components/ui";

/** App string overrides (02 §16 "translations"): every key the apps ship, the
 * English text, and an optional override per locale. Empty override = bundled text. */
export function TranslationsPage() {
  const [tick, setTick] = useState(0);
  const locales = useApiList<LocaleSummary & { id: string }>("/admin/translations", { _t: tick });
  const [locale, setLocale] = useState("en");
  const [entries, setEntries] = useState<TranslationEntry[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const { busy, run } = useMutation();
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api<TranslationEntry[]>(`/admin/translations/${locale}`)
      .then(({ data }) => {
        if (cancelled) return;
        setEntries(data);
        setDraft({});
      })
      .catch((e: unknown) => toast(e instanceof Error ? e.message : "could not load", "err"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, tick]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((e) => e.key.toLowerCase().includes(needle) || (e.default ?? "").toLowerCase().includes(needle) || (e.value ?? "").toLowerCase().includes(needle));
  }, [entries, q]);

  const changed = Object.keys(draft).length;

  async function save() {
    const r = await run<{ written: number; deleted: number }>(`/admin/translations/${locale}`, { method: "PUT", body: { strings: draft } });
    if (r.ok) {
      toast(`Saved: ${r.data.written} set, ${r.data.deleted} cleared`);
      setTick((t) => t + 1);
    } else toast(r.error, "err");
  }

  return (
    <Page
      title="Translations"
      actions={
        <button type="button" className="btn btn-primary" disabled={!changed || busy} onClick={save}>
          {busy ? "Saving…" : `Save ${changed || ""} change${changed === 1 ? "" : "s"}`}
        </button>
      }
    >
      <Toolbar>
        <select className="input" value={locale} onChange={(e) => setLocale(e.target.value)}>
          {locales.data.map((l) => (
            <option key={l.code} value={l.code}>
              {l.code} · {l.overrides} override{l.overrides === 1 ? "" : "s"}
            </option>
          ))}
          {locales.data.length === 0 && <option value="en">en</option>}
        </select>
        <input className="input" placeholder="Filter keys or text" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="muted">
          {entries.length} keys · the apps use the bundled text where the override is empty
        </span>
      </Toolbar>
      {loading && entries.length === 0 ? (
        <p className="muted">Loading…</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Key</th>
              <th>English (bundled)</th>
              <th>Override for {locale}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((e) => {
              const current = e.key in draft ? draft[e.key] : (e.value ?? "");
              return (
                <tr key={e.key}>
                  <td className="mono">
                    {e.key}
                    {e.default == null && (
                      <>
                        {" "}
                        <Badge tone="warn">not in app</Badge>
                      </>
                    )}
                  </td>
                  <td>{e.default ?? <span className="muted">—</span>}</td>
                  <td>
                    <input
                      className="input"
                      style={{ width: "100%" }}
                      value={current}
                      placeholder={e.default ?? ""}
                      onChange={(ev) => {
                        const v = ev.target.value;
                        setDraft((d) => {
                          const next = { ...d };
                          if (v === (e.value ?? "")) delete next[e.key];
                          else next[e.key] = v;
                          return next;
                        });
                      }}
                    />
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={3} className="muted">
                  No keys match
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </Page>
  );
}
